<script setup lang="ts">
import BaseIcon from '@/components/ui/BaseIcon.vue';
import BottomSheet, { type BottomSheetPosition } from '@/components/layout/BottomSheet.vue';
import BookmarkButton from '@/components/ui/BookmarkButton.vue';
import ListItem from '@/components/ui/ListItem.vue';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import gtfsHelper from '@/libs/gtfsHelper';
import { dedupeAlerts } from '@/libs/alerts-helper';
import { AppRoute } from '@/libs/routing';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { findNearestStop, ROUTE_NEAREST_STOP_RADIUS_M } from '@/libs/helpers/nearby-stop';
import type { Alert } from '@/@types/alert';
import type { DeviceCount } from '@/@types/device';
import type { Route, Shape, Stop, Trip } from '@/@types/gtfs-ressource';
import { useGtfsStore, type Bounds } from '@/stores/gtfs';
import { useDevicesCountPolling } from '@/use/devices-count-polling';
import { useDevicesStore } from '@/stores/devices';
import { MapFocusType, useAppStore } from '@/stores/app';
import { useAlertsStore } from '@/stores/alerts';

const devicesStore = useDevicesStore();
const gtfsStore = useGtfsStore();
const appStore = useAppStore();
const alertsStore = useAlertsStore();

const props = defineProps<{
  routeId: string;
}>();

// Opens on the reading snap: the detail is what the passenger came for, the map is context.
const sheetPosition = ref<BottomSheetPosition>('intermediate');
const panelEl = ref<HTMLElement>();
const hasScrolledToNearestStop = ref<boolean>(false);

const routeTripsOfDay = computed<Array<Trip>>(() => gtfsStore.getRouteTripsOfDay(props.routeId));
const now = computed<number>(() => Date.now() / 1000);
const route = computed<Route>(() => gtfsStore.gtfsData.routes[props.routeId] || {});
const routeEndService = computed<number>(() => gtfsStore.getRouteEndService(props.routeId));
const routeLongName = computed<string | undefined>(() => route.value && route.value.route_long_name);

const routeStops = computed<{ [stopId: string]: boolean } | undefined>(
  () => gtfsStore.gtfsData.routeStops?.[props.routeId],
);
const shapes = computed<{ [shapeId: string]: Shape }>(() => gtfsStore.gtfsData.shapes);
const stops = computed<{ [stopId: string]: Stop }>(() => gtfsStore.gtfsData.stops);

const routeAlerts = computed<Array<Alert>>(() =>
  dedupeAlerts(
    alertsStore.alerts.filter(alert =>
      alert.informed_entity?.some(informedEntity => informedEntity.route_id === props.routeId),
    ),
  ),
);

// Several alerts collapse into a single banner: the first one plus a counter.
const mainAlert = computed<Alert | undefined>(() => routeAlerts.value[0]);

const deviceCount = computed<DeviceCount | undefined>(() =>
  devicesStore.onlineDevicesCount.find(counter => counter.route_id === props.routeId),
);

const hasServiceToday = computed<boolean>(() => routeTripsOfDay.value.length > 0);
const isEndOfService = computed<boolean>(() => hasServiceToday.value && now.value > routeEndService.value);

const shownStops = computed<{ [stopId: string]: Stop }>(() => {
  if (!routeStops.value) return stops.value;

  const shownStops: { [stopId: string]: Stop } = {};
  Object.keys(routeStops.value).forEach(stopId => {
    if (!shownStops[stopId]) {
      shownStops[stopId] = stops.value[stopId];
      const canonicalId = gtfsStore.getCanonicalStopId(stopId);
      if (!shownStops[canonicalId] && stops.value[canonicalId]) {
        shownStops[canonicalId] = stops.value[canonicalId];
      }
    }
  });
  return shownStops;
});

const stopList = computed<Array<Stop>>(() =>
  routeStops.value
    ? gtfsHelper.listRouteStopsByName({
        routeStops: routeStops.value,
        stops: stops.value,
        canonicalOf: gtfsStore.canonicalOf,
      })
    : [],
);

// Never triggers a prompt: the position is only read when already granted
const nearestStopId = computed<string | null>(() => {
  const position = appStore.positionGeolocation;
  if (!position) return null;
  return findNearestStop(position, stopList.value, ROUTE_NEAREST_STOP_RADIUS_M)?.stop_id ?? null;
});

watch(
  () => props.routeId,
  () => {
    showShapes();
    appStore.setLastSelectedRouteId(props.routeId);
    hasScrolledToNearestStop.value = false;
  },
  { immediate: true },
);

watch(
  () => shapes.value,
  () => {
    showShapes();
  },
  { immediate: true },
);

watch(
  () => shownStops.value,
  () => {
    gtfsStore.setShownStops(shownStops.value);
  },
  { immediate: true },
);

// Scroll only on the first match, then leave the list where the passenger put it.
watch(
  () => nearestStopId.value,
  async stopId => {
    if (!stopId || hasScrolledToNearestStop.value) return;
    hasScrolledToNearestStop.value = true;
    await nextTick();
    panelEl.value?.querySelector('.route-panel__stop--nearest')?.scrollIntoView({ block: 'start' });
  },
  { immediate: true },
);

useDevicesCountPolling([props.routeId]);

onMounted(() => {
  appStore.getGeolocationIfAllowed();
});

function showShapes() {
  const shownShapes: { [shapeId: string]: Shape } = {};
  let bounds: Partial<Bounds> = {};

  for (const shapeId in shapes.value) {
    const shape = shapes.value[shapeId];
    const shapeRouteIds = shape.routeIds ?? (shape.routeId ? [shape.routeId] : []);

    if (shapeRouteIds.includes(props.routeId)) {
      shownShapes[shapeId] = { ...shape, routeId: props.routeId };
      shape.geometry.coordinates.forEach(coordinate => {
        if (!bounds.south || coordinate[1] < bounds.south) {
          bounds.south = coordinate[1];
        }
        if (!bounds.north || coordinate[1] > bounds.north) {
          bounds.north = coordinate[1];
        }
        if (!bounds.west || coordinate[0] < bounds.west) {
          bounds.west = coordinate[0];
        }
        if (!bounds.east || coordinate[0] > bounds.east) {
          bounds.east = coordinate[0];
        }
      });
    }
  }

  if (!bounds.north && !bounds.south && !bounds.east && !bounds.west) {
    bounds = gtfsStore.networkMapBounds;
  }

  gtfsStore.setShownShapes(shownShapes);

  appStore.setMapFocus({
    type: MapFocusType.BOUNDS,
    elem: bounds as Bounds,
  });
}
</script>

<template>
  <div ref="panelEl" class="route-panel">
    <BottomSheet v-model:position="sheetPosition" @update:height="appStore.setMapBottomInset">
      <template #header>
        <div class="route-panel__header">
          <div class="route-panel__identity">
            <div class="route-panel__titles">
              <RouteBadge :route="route" />
              <div class="route-panel__name">{{ routeLongName }}</div>
            </div>
            <span class="route-panel__bookmark">
              <BookmarkButton :element="route" />
            </span>
          </div>

          <router-link
            v-if="mainAlert"
            class="route-panel__alert"
            :to="{ name: AppRoute.ROUTE_ALERTS, params: { routeId: routeId } }"
          >
            <BaseIcon name="info" class="route-panel__alert-icon" />
            <span class="route-panel__alert-text">{{ mainAlert.header_text }}</span>
            <span v-if="routeAlerts.length > 1" class="route-panel__alert-count">
              +{{ routeAlerts.length - 1 }}
            </span>
          </router-link>

          <div class="route-panel__status">
            <template v-if="deviceCount">
              <BaseIcon
                name="rss"
                :stroke-width="3"
                class="route-panel__status-icon route-panel__status-icon--realtime"
              />
              <span class="route-panel__status-text">
                {{ $tc('routes.connectedVehicles', deviceCount.count) }}
              </span>
            </template>

            <template v-else-if="!hasServiceToday || isEndOfService">
              <BaseIcon name="moon" class="route-panel__status-icon route-panel__status-icon--ended" />
              <span class="route-panel__status-text">
                {{ hasServiceToday ? $t('common.endOfService') : $t('common.noServiceToday') }}
              </span>
            </template>

            <span v-else class="route-panel__status-text">{{ $t('routes.noVehicle') }}</span>
          </div>
        </div>
      </template>

      <router-link
        v-for="stop in stopList"
        :key="stop.stop_id"
        class="route-panel__stop"
        :class="{ 'route-panel__stop--nearest': stop.stop_id === nearestStopId }"
        :to="{ name: AppRoute.STOP_DETAILED, params: { stopId: stop.stop_id }, query: { route: routeId } }"
      >
        <ListItem :label="stop.stop_name">
          <template v-if="stop.stop_id === nearestStopId" #label-suffix>
            <span class="route-panel__stop-nearest">
              <BaseIcon name="map-pin" class="route-panel__stop-nearest-icon" />
              {{ $t('routes.nearestStop') }}
            </span>
          </template>
          <template #trailing>
            <BaseIcon name="chevron-right" class="route-panel__stop-chevron" />
          </template>
        </ListItem>
      </router-link>
    </BottomSheet>
  </div>
</template>

<style lang="scss">
.route-panel {
  @include sheet-host;

  &__header {
    border-bottom: 1px solid $color-border-light;
    display: flex;
    flex-direction: column;
    gap: $spacing-5;
    padding: $spacing-4 $spacing-4 $spacing-5;
  }

  &__identity {
    align-items: flex-start;
    display: flex;
    gap: $spacing-4;
  }

  &__titles {
    // The badge hugs its label; only the name spans the column
    align-items: flex-start;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
  }

  &__name {
    align-self: stretch;
    color: $color-neutral-highest;
    display: -webkit-box;
    font: 600 24px / 32px $font-poppins;
    letter-spacing: $letter-spacing-tight;
    // A very long route_long_name wraps onto 2 lines, then clips
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }

  &__bookmark {
    @include icon-pill;
  }

  &__alert {
    align-items: center;
    background-color: $color-warning-light;
    border-radius: 16px;
    color: $color-warning-dark;
    display: flex;
    gap: $spacing-2;
    padding: $spacing-4;

    &:active {
      filter: brightness(0.97);
    }
  }

  &__alert-icon {
    flex-shrink: 0;
    font-size: 24px;
  }

  &__alert-text {
    display: -webkit-box;
    flex: 1;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    min-width: 0;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }

  &__alert-count {
    flex-shrink: 0;
    font: $text-bold-small;
    letter-spacing: $text-bold-small-spacing;
  }

  &__status {
    align-items: center;
    display: flex;
    gap: $spacing-1;
  }

  &__status-icon {
    flex-shrink: 0;
    font-size: 20px;

    &--realtime {
      color: $color-primary;
      transform: rotate(270deg);
    }

    &--ended {
      color: $color-secondary;
      font-variation-settings: 'FILL' 1;
    }
  }

  &__status-text {
    color: $color-neutral-higher;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
  }

  &__stop-chevron {
    color: $color-neutral-higher;
    flex-shrink: 0;
    font-size: 20px;
  }

  &__stop {
    display: block;

    &:active {
      background-color: $color-neutral-lower;
    }
  }

  // Rides on the stop name's own line: inline, never split, never wider than needed.
  &__stop-nearest {
    align-items: center;
    background-color: $color-neutral-low;
    border-radius: $radius-pill;
    color: $color-text-dark;
    display: inline-flex;
    font: $text-smaller;
    gap: $spacing-1;
    letter-spacing: $text-smaller-spacing;
    margin-left: $spacing-1;
    padding: 2px 6px 2px 6px;
    vertical-align: middle;
    white-space: nowrap;
  }

  &__stop-nearest-icon {
    font-size: 12px;
  }
}
</style>
